import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import clsx from 'clsx'
import { useStaticQuery, graphql } from 'gatsby'
import { Container } from 'chaoskit/src/components'
import { text } from 'chaoskit/src/assets/styles/utility'

import Data from '~components/Data'
import ButtonLink from '~components/ButtonLink'
import mascotArms from '~media/global/mascot-arms.svg'
import background from '~media/global/slant-background.svg'

export const hangingGremlinVariables = {
  height: 80,
  get offset() {
    return this.height / 1.35
  },
}

// 1. Offset for gremlin to appear "on-top" of element
export const StylesCTAGremlin = {
  content: "''",
  top: -hangingGremlinVariables.offset, // 1
  height: hangingGremlinVariables.height,
  position: 'absolute',
  width: '100%',
  right: 0,
  background: `url(${mascotArms}) no-repeat top right`,
  zIndex: 1,
}

const ArticleCTAContent = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        paddingTop: theme.space.large,
        paddingBottom: theme.space.large,
        paddingLeft: theme.space.large - theme.space.base,
        paddingRight: theme.space.large - theme.space.base,
        display: 'grid',
        alignItems: 'center',
        gap: theme.space.large,
        borderRadius: theme.borderRadius.base,
        position: 'relative',
        boxShadow: theme.boxShadow.large,

        [theme.mq.medium]: {
          padding: theme.space.xlarge,
          gridTemplateColumns: '1fr auto',
          gap: theme.space.xlarge,
        },

        '.is-white &': {
          border: `1px solid ${theme.color.primary.base}`,
          background: theme.color.light.base,

          [theme.mq.small__max]: {
            gap: theme.space.base + hangingGremlinVariables.offset,
          },
        },

        '.is-green &': {
          background: `url(${background}) no-repeat center center, linear-gradient(to right, ${theme.brand.teal} 50%, ${theme.color.primary.base})`,
          backgroundSize: 'cover',

          '&::before': StylesCTAGremlin,
        },
      }}
      {...props}
    />
  )
}

export const InlineArticleCTAWrapper = ({ children, variation }) => {
  const theme = useTheme()

  return (
    <aside
      css={[
        {
          position: 'relative',
          marginTop: theme.space.large,
          marginBottom: theme.space.large,

          '&.is-green': {
            marginTop: hangingGremlinVariables.height,
          },
        },
      ]}
      className={clsx({
        'is-green u-contrast': variation === 'green',
        'is-white': variation === 'white',
      })}
    >
      <Container>
        <ArticleCTAContent>{children}</ArticleCTAContent>
      </Container>
    </aside>
  )
}

InlineArticleCTAWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  variation: PropTypes.oneOf(['green', 'white']).isRequired,
}

export const InlineArticleCTAButtonWrapper = ({ children }) => {
  const theme = useTheme()

  return (
    <div>
      <div
        css={{
          position: 'relative',

          '.is-white &': {
            '&::before': {
              ...StylesCTAGremlin,
              right: theme.space.base,
              pointerEvents: 'none',
            },

            [theme.mq.small__max]: {
              display: 'inline-flex',
              left: '50%',
              transform: 'translateX(-50%)',
            },
          },
        }}
      >
        {children}
      </div>
    </div>
  )
}

InlineArticleCTAButtonWrapper.propTypes = {
  children: PropTypes.node.isRequired,
}

export const InlineArticleCTAContentWrapper = (props) => {
  const theme = useTheme()

  return (
    <div
      css={[
        text.heading(theme),
        {
          fontSize: theme.fontSize.medium,
          lineHeight: theme.lineHeight.base,
        },
      ]}
      {...props}
    />
  )
}

InlineArticleCTAContentWrapper.propTypes = {
  children: PropTypes.node.isRequired,
}

const AsideArticleCTA = ({ data }) => {
  const theme = useTheme()

  return (
    <aside css={{ display: 'grid', gap: theme.space.base }}>
      <div css={[text.heading(theme), theme.fontSize.h3__fluid]}>
        {data.heading}
      </div>
      <div css={{ color: theme.fontColor.muted }}>
        <Data markdown>{data.content}</Data>
      </div>
      <div>
        <ButtonLink type="primary" url={data.button.url}>
          {data.button.label}
        </ButtonLink>
      </div>
    </aside>
  )
}

AsideArticleCTA.propTypes = {
  data: PropTypes.object.isRequired,
}

const ArticleCTA = ({ cta, collection, type = 'inline' }) => {
  const {
    file: {
      childMdx: { frontmatter: defaultCTAs },
    },
  } = useStaticQuery(graphql`
    query ArticleCTAData {
      file(
        name: { eq: "article-ctas" }
        absolutePath: { regex: "/src/data/" }
      ) {
        childMdx {
          frontmatter {
            defaultBlogCTA {
              ...ArticleCTAFragment
            }
            defaultTutorialCTA {
              ...ArticleCTAFragment
            }
          }
        }
      }
    }
  `)

  // Get fallback type
  // @NOTE Needed below if nothing is returned from component-call "cta"
  const defaultType =
    collection === 'blog' ? 'defaultBlogCTA' : 'defaultTutorialCTA'

  // Find chosen CTA OR fallback to default based on "type" provided
  const chosenCTA = cta ? cta.frontmatter : defaultCTAs[defaultType].frontmatter

  if (type === 'inline') {
    return (
      <InlineArticleCTAWrapper variation={chosenCTA.variation}>
        <InlineArticleCTAContentWrapper>
          <Data markdown>{chosenCTA.content}</Data>
        </InlineArticleCTAContentWrapper>
        <InlineArticleCTAButtonWrapper>
          <ButtonLink type="primary" url={chosenCTA.button.url}>
            {chosenCTA.button.label}
          </ButtonLink>
        </InlineArticleCTAButtonWrapper>
      </InlineArticleCTAWrapper>
    )
  }

  return <AsideArticleCTA data={chosenCTA} />
}

ArticleCTA.propTypes = {
  cta: PropTypes.object,
  type: PropTypes.oneOf(['inline', 'aside']).isRequired,
  collection: PropTypes.oneOf(['blog', 'tutorials']),
}

export default ArticleCTA
